import { User, Country } from '.';
import { Money } from './Money';

export interface LocationSpecificSurveyInput {
  /* meta data */
  lastUpdatedDate: number;
  lastUpdatedUser: User;

  organizationId: number;
  organizationName: string;

  country: Country;

  surveySubmissionId: number;

  /* responses */

  /* step 1 */

  officeAddress: {
    lastUpdatedDate: number;
    lastUpdatedUser: User;
    updatedBySRAdmin: boolean;

    value: {
      address1: string;
      address2: string;
      addressId: number;
      city: string;
      state: string;
    };
  };

  activityHistory: {
    lastUpdatedDate: number;
    lastUpdatedUser: User;
    updatedBySRAdmin: boolean;

    value: string;
  };

  activityHistoryVideoUrl: {
    lastUpdatedDate: number;
    lastUpdatedUser: User;
    updatedBySRAdmin: boolean;

    value: string;
  };

  currentProgramVideoUrl: {
    lastUpdatedDate: number;
    lastUpdatedUser: User;
    updatedBySRAdmin: boolean;

    value: string;
  };

  startOpDate: {
    lastUpdatedDate: number;
    lastUpdatedUser: User;
    updatedBySRAdmin: boolean;

    value: string;
  };

  repContact: {
    lastUpdatedDate: number;
    lastUpdatedUser: User;
    updatedBySRAdmin: boolean;

    value: {
      alternativeEmail: string;
      email: string;
      firstName: string;
      lastName: string;
      phone1: string;
      title: string;
      country: Country;
    };
  };

  govIdOrEin: {
    lastUpdatedDate: number;
    lastUpdatedUser: User;
    updatedBySRAdmin: boolean;
    markAsUpdated: boolean;
    value: string;
  };

  govIdOrEin2: {
    lastUpdatedDate: number;
    lastUpdatedUser: User;
    updatedBySRAdmin: boolean;
    markAsUpdated: boolean;
    value: string;
  };

  /* step 2 */

  serviceList: {
    lastUpdatedDate: number;
    lastUpdatedUser: User;
    updatedBySRAdmin: boolean;

    value: {
      directProportion: number;
      indirectProportion: number;
      serviceProvided: {
        serviceId: number;
        name: string;
      };
    }[];
  };

  reliefTypeList: {
    lastUpdatedDate: number;
    lastUpdatedUser: User;
    updatedBySRAdmin: boolean;

    value: {
      itemCount: number;
      typeId: number;
    }[];
  };

  acceptDonationsOfImportedGoods: {
    lastUpdatedDate: number;
    lastUpdatedUser: User;
    updatedBySRAdmin: boolean;

    value: {
      acceptImportedGoods: boolean;
      hasPreviouslyAcceptedImportedGoods: boolean;
      importerOfRecord: string;
    };
  };

  disasterStageOfWork: {
    lastUpdatedDate: number;
    lastUpdatedUser: User;
    updatedBySRAdmin: boolean;

    value: {
      mitigationStage: boolean;
      reliefStage: boolean;
      recoveryStage: boolean;
      responseStage: boolean;
      preparedStage: boolean;
    };
  };

  disasterResponsePlan: {
    lastUpdatedDate: number;
    lastUpdatedUser: User;
    updatedBySRAdmin: boolean;

    value: {
      hasValue: boolean;
      fileId: number;
      webLink: string;
    };
  };

  /* step 3 */

  localStaff: {
    lastUpdatedDate: number;
    lastUpdatedUser: User;
    updatedBySRAdmin: boolean;

    value: {
      mgmtStaffCount: number;
      professionalStaffCount: number;
      supportStaffCount: number;
    };
  };

  nonLocalStaff: {
    lastUpdatedDate: number;
    lastUpdatedUser: User;
    updatedBySRAdmin: boolean;

    value: {
      mgmtStaffCount: number;
      professionalStaffCount: number;
      supportStaffCount: number;
    };
  };

  volunteerStaff: {
    lastUpdatedDate: number;
    lastUpdatedUser: User;
    updatedBySRAdmin: boolean;

    value: {
      mgmtStaffCount: number;
      professionalStaffCount: number;
      supportStaffCount: number;
    };
  };

  /* step 4 */

  overallBudget: {
    lastUpdatedDate: number;
    lastUpdatedUser: User;
    updatedBySRAdmin: boolean;

    value: Money;
  };

  fiscalYearEnding: {
    lastUpdatedDate: number;
    lastUpdatedUser: User;
    updatedBySRAdmin: boolean;

    value: string;
  };

  annualReport: {
    lastUpdatedDate: number;
    lastUpdatedUser: User;
    updatedBySRAdmin: boolean;

    value: {
      hasValue: boolean;
      fileId: number;
      webLink: string;
    };
  };

  /* step 5 */

  monitorProtocol: {
    lastUpdatedDate: number;
    lastUpdatedUser: User;
    updatedBySRAdmin: boolean;

    value: {
      hasValue: boolean;
      fileId: number;
      webLink: string;
    };
  };

  engagement: {
    lastUpdatedDate: number;
    lastUpdatedUser: User;
    updatedBySRAdmin: boolean;

    value: {
      hasEngagement: boolean;
      engagementList: string[];
    };
  };

  membershipList: {
    lastUpdatedDate: number;
    lastUpdatedUser: User;
    updatedBySRAdmin: boolean;

    value: string[];
  };

  vettingAgencies: {
    lastUpdatedDate: number;
    lastUpdatedUser: User;
    updatedBySRAdmin: boolean;

    value: [
      {
        agencyId: number;
        agencyName: string;
        expirationDate: string;
        startDate: string;
        vettingAgencyLetterDtos: [{ fileId: number }];
      }
    ];
  };

  /* step 6 */

  contractManagement: {
    lastUpdatedDate: number;
    lastUpdatedUser: User;
    updatedBySRAdmin: boolean;

    value: string;
  };

  preventCorruption: {
    lastUpdatedDate: number;
    lastUpdatedUser: User;
    updatedBySRAdmin: boolean;

    value: string;
  };

  acceptGovFund: {
    lastUpdatedDate: number;
    lastUpdatedUser: User;
    updatedBySRAdmin: boolean;

    value: boolean;
  };

  disabilityPhysicalAccess: {
    lastUpdatedDate: number;
    lastUpdatedUser: User;
    updatedBySRAdmin: boolean;

    value: string;
  };

  disabilityProgramAccess: {
    lastUpdatedDate: number;
    lastUpdatedUser: User;
    updatedBySRAdmin: boolean;

    value: string;
  };

  disabilityCommunicationAccess: {
    lastUpdatedDate: number;
    lastUpdatedUser: User;
    updatedBySRAdmin: boolean;

    value: string;
  };

  childProtectionEmergencyPlan: {
    lastUpdatedDate: number;
    lastUpdatedUser: User;
    updatedBySRAdmin: boolean;

    value: {
      hasValue: boolean;
      fileId: number;
      webLink: string;
      comment: string;
    };
  };

  organizationDto: {
    country: string;
  };

  donateButtonDisabled: boolean;

  // step 7

  operatingLocationList: {
    lastUpdatedDate: number;
    lastUpdatedUser: User;
    updatedBySRAdmin: boolean;

    value: [
      {
        geoLatitude: number;
        geoLongitude: number;
        radius: number;
      }
    ];
  };

  operatingInEntireCountry: boolean;
}

export interface LocationSpecificSurveyOutput {
  /* meta data */

  previousSurveyId: number;

  /* responses */

  /* step 1 */

  officeAddress: {
    markAsUpdated: boolean;
    value: {
      address1: string;
      address2: string;
      city: string;
      state: string;
    };
  };

  activityHistory: {
    markAsUpdated: boolean;
    value: string;
  };

  activityHistoryVideoUrl: {
    markAsUpdated: boolean;
    value: string;
  };

  currentProgramVideoUrl: {
    markAsUpdated: boolean;
    value: string;
  };

  startOpDate: {
    markAsUpdated: boolean;
    value: string;
  };

  repContact: {
    markAsUpdated: boolean;
    value: {
      alternativeEmail: string;
      email: string;
      firstName: string;
      lastName: string;
      phone1: string;
      title: string;
      country: Country;
    };
  };

  govIdOrEin: {
    markAsUpdated: boolean;
    value: string;
  };

  govIdOrEin2: {
    markAsUpdated: boolean;
    value: string;
  };

  /* step 2 */

  serviceList: {
    markAsUpdated: boolean;
    value: [
      {
        directProportion: number;
        indirectProportion: number;
        serviceProvided: {
          serviceId: number;
        };
      }
    ];
  };

  reliefTypeList: {
    markAsUpdated: boolean;
    value: [
      {
        itemCount: number;
        typeId: number;
      }
    ];
  };

  acceptDonationsOfImportedGoods: {
    markAsUpdated: boolean;
    value: {
      acceptImportedGoods: boolean;
      hasPreviouslyAcceptedImportedGoods: boolean;
      importerOfRecord: string;
    };
  };

  disasterStageOfWork: {
    markAsUpdated: boolean;
    value: {
      mitigationStage: boolean;
      reliefStage: boolean;
      recoveryStage: boolean;
      responseStage: boolean;
      preparedStage: boolean;
    };
  };

  disasterResponsePlan: {
    markAsUpdated: boolean;
    value: {
      hasValue: boolean;
      fileId: number;
      webLink: string;
    };
  };

  /* step 3 */

  localStaff: {
    markAsUpdated: boolean;
    value: {
      mgmtStaffCount: number;
      professionalStaffCount: number;
      supportStaffCount: number;
    };
  };

  nonLocalStaff: {
    markAsUpdated: boolean;
    value: {
      mgmtStaffCount: number;
      professionalStaffCount: number;
      supportStaffCount: number;
    };
  };

  volunteerStaff: {
    markAsUpdated: boolean;
    value: {
      mgmtStaffCount: number;
      professionalStaffCount: number;
      supportStaffCount: number;
    };
  };

  /* step 4 */

  fiscalYearEnding: {
    markAsUpdated: boolean;
    value: string;
  };

  overallBudget: {
    markAsUpdated: boolean;
    value: Money;
  };

  annualReport: {
    markAsUpdated: boolean;
    value: {
      hasValue: boolean;
      fileId: number;
      webLink: string;
    };
  };

  /* step 5 */

  monitorProtocol: {
    markAsUpdated: boolean;
    value: {
      hasValue: boolean;
      fileId: number;
      webLink: string;
    };
  };

  engagement: {
    markAsUpdated: boolean;
    value: {
      engagementList: string[];
      hasEngagement: boolean;
    };
  };

  membershipList: {
    markAsUpdated: boolean;
    value: string[];
  };

  vettingAgencies: {
    markAsUpdated: boolean;
    value: [
      {
        agencyId: number;
        agencyName: string;
        expirationDate: string;
        startDate: string;
        vettingAgencyLetterDtos: [
          {
            fileId: number;
          }
        ];
      }
    ];
  };

  /* step 6 */

  contractManagement: {
    markAsUpdated: boolean;
    value: string;
  };

  preventCorruption: {
    markAsUpdated: boolean;
    value: string;
  };

  acceptGovFund: {
    markAsUpdated: boolean;
    value: boolean;
  };

  disabilityPhysicalAccess: {
    markAsUpdated: boolean;
    value: string;
  };

  disabilityProgramAccess: {
    markAsUpdated: boolean;
    value: string;
  };

  disabilityCommunicationAccess: {
    markAsUpdated: boolean;
    value: string;
  };

  childProtectionEmergencyPlan: {
    markAsUpdated: boolean;
    value: {
      hasValue: boolean;
      fileId: number;
      webLink: string;
      comment: string;
    };
  };

  /* Step 7 */
  operatingLocationList?: {
    markAsUpdated: boolean;
    value: [
      {
        geoLatitude: number;
        geoLongitude: number;
        radius: number;
      }
    ];
  };

  operatingInEntireCountry?: boolean;
}

export interface LocationSpecificSurveyServiceListValue {
  directProportion: number;

  indirectProportion: number;

  serviceProvided: {
    serviceId: number;
    name: string;
  };
}
